import { CheckCircle, X, XCircle } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
	removeToast,
	type ToastItem,
} from "../../../features/toast/toast.slice";
import { cn } from "../../../lib/utils";

interface ToastProps {
	toast: ToastItem;
}

export const Toast = ({ toast }: ToastProps) => {
	const dispatch = useDispatch();

	const dismiss = useCallback(() => {
		dispatch(removeToast(toast.id));
	}, [dispatch, toast.id]);

	useEffect(() => {
		const timer = window.setTimeout(dismiss, toast.duration ?? 4000);
		return () => window.clearTimeout(timer);
	}, [dismiss, toast.duration]);

	const isSuccess = toast.type === "success";

	return (
		<div
			role="alert"
			aria-live="assertive"
			className={cn(
				"flex items-start gap-3 rounded-xl px-4 py-3.5 shadow-lg",
				"min-w-[280px] max-w-[420px] w-full",
				"animate-in slide-in-from-right-4 fade-in duration-300",
				isSuccess
					? "bg-[#e6f5f2] border border-[#007955]/20 text-[#005c3f]"
					: "bg-[#fef2f2] border border-red-200 text-red-800",
			)}
		>
			<span className="mt-0.5 shrink-0">
				{isSuccess ? (
					<CheckCircle className="h-5 w-5 text-[#007955]" />
				) : (
					<XCircle className="h-5 w-5 text-red-500" />
				)}
			</span>
			<p className="flex-1 text-sm font-medium leading-snug">{toast.message}</p>
			<button
				type="button"
				onClick={dismiss}
				aria-label="Dismiss notification"
				className={cn(
					"mt-0.5 shrink-0 rounded p-0.5 transition-colors",
					isSuccess ? "hover:bg-[#007955]/10" : "hover:bg-red-100",
				)}
			>
				<X className="h-4 w-4 opacity-60" />
			</button>
		</div>
	);
};
